import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const categoryName = category?.name || article.category;
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`捆绑调教,${categoryName},捆绑调教技巧,捆绑调教安全须知,BDSM捆绑指南`}
        canonicalUrl={`/article/${slug}`}
        ogType="article"
        ogImage={article.image}
        jsonLdScripts={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.image,
            datePublished: article.date,
            articleSection: categoryName,
            wordCount: article.readTime * 500,
            inLanguage: 'zh-CN',
            isPartOf: { '@type': 'WebSite', name: '捆绑调教完全指南', url: 'https://bondage-guide.com' },
            publisher: { '@type': 'Organization', name: '捆绑调教完全指南' },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://bondage-guide.com/' },
              { '@type': 'ListItem', position: 2, name: categoryName, item: `https://bondage-guide.com/category/${article.category}` },
              { '@type': 'ListItem', position: 3, name: article.title, item: `https://bondage-guide.com/article/${slug}` },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: article.title,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: article.excerpt,
                },
              },
            ],
          },
        ]}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-24 pb-16 bg-background-50 min-h-screen">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: categoryName, path: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="text-xs font-medium px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 whitespace-nowrap cursor-pointer"
              >
                {categoryName}
              </Link>
              <time dateTime={article.date} className="text-xs text-foreground-500">{article.date}</time>
              <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>
          </header>

          <div className="mb-10 rounded-xl overflow-hidden">
            <div className="aspect-[21/9]">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div
            className="prose prose-lg max-w-none prose-headings:text-foreground-950 prose-headings:font-semibold prose-h2:text-xl md:prose-h2:text-2xl prose-h3:text-lg md:prose-h3:text-xl prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-base prose-strong:text-foreground-950 prose-li:text-foreground-700"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-foreground-500">标签：</span>
              <span className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">捆绑调教</span>
              <span className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">{categoryName}</span>
              <span className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">SSC原则</span>
              <span className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">成人伴侣</span>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">
              相关文章
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map((related) => (
                <ArticleCard
                  key={related.slug}
                  slug={related.slug}
                  title={related.title}
                  excerpt={related.excerpt}
                  category={related.category}
                  date={related.date}
                  readTime={related.readTime}
                  image={related.image}
                />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}